import { useMemo } from 'react';
import {
	BigNum,
	getUser30dRollingVolumeEstimate,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import {
	STAKED_TIERS,
	VOLUME_TIERS,
} from 'src/components/Modals/FeeTierModal/consts';
import { DRIFT_TOKEN_BANK_INDEX } from 'src/environmentVariables/EnvironmentVariables';
import { useIfUserStake } from 'src/hooks/query/ifData/useIfDataQuery';
import useRootAuthorityUserStatsAccount from 'src/hooks/query/authority/useRootAuthorityUserStatsAccount';

/**
 * Determines the user's current tier indexes & amounts.
 */
export default function useUserFeetier() {
	const { data: userStatsAccount, isLoading: isLoadingUserStatsAccount } =
		useRootAuthorityUserStatsAccount();

	const { userVolumeTierIndex, userCurrent30dVolume } = useMemo(() => {
		if (!userStatsAccount) {
			return {
				userVolumeTierIndex: -1,
				userCurrent30dVolume: BigNum.zero(),
			};
		}

		const userCurrent30dVolume = BigNum.from(
			getUser30dRollingVolumeEstimate(userStatsAccount),
			QUOTE_PRECISION_EXP
		);

		let userVolumeTierIndex = 0;

		for (let i = VOLUME_TIERS.length - 1; i >= 0; i--) {
			if (userCurrent30dVolume.toNum() >= VOLUME_TIERS[i].amount) {
				userVolumeTierIndex = i;
				break;
			}
		}

		return { userVolumeTierIndex, userCurrent30dVolume };
	}, [userStatsAccount]);

	const driftUserStake = useIfUserStake(DRIFT_TOKEN_BANK_INDEX);
	const userCurrentStakedAmountIfData = driftUserStake?.currentStake;

	const { userStakedTierIndex, userCurrentStakedAmount } = useMemo(() => {
		if (userCurrentStakedAmountIfData === undefined) {
			return {
				userStakedTierIndex: -1,
				userCurrentStakedAmount: 0,
			};
		}

		let userStakedTierIndex = 0;

		for (let i = STAKED_TIERS.length - 1; i >= 0; i--) {
			if (userCurrentStakedAmountIfData >= STAKED_TIERS[i].amount) {
				userStakedTierIndex = i;
				break;
			}
		}

		return {
			userStakedTierIndex,
			userCurrentStakedAmount: userCurrentStakedAmountIfData,
		};
	}, [userCurrentStakedAmountIfData]);

	return {
		userVolumeTierIndex,
		userCurrent30dVolume,
		userStakedTierIndex,
		userCurrentStakedAmount,
		isLoadingVolume: isLoadingUserStatsAccount,
	};
}
